/**
 * Corporate API Service
 * 
 * RTK Query service for corporate account operations and analytics
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { env } from '../config/environment'

export interface CorporateMetrics {
  totalRevenue: number
  totalJobs: number
  totalCustomers: number
  totalInvoices: number
  businessCount: number
  averageRevenuePerBusiness: number
}

export interface BusinessMetrics {
  businessId: string
  businessName: string
  logo: string | null
  revenue: number
  jobs: number
  customers: number
  invoices: number
  activeUsers: number
}

export interface DashboardData {
  success: boolean
  summary: {
    totalRevenue: number
    totalJobs: number
    totalCustomers: number
    totalInvoices: number
    businessCount: number
  }
  businesses: Array<{
    business: {
      id: string
      name: string
      logo: string | null
    }
    metrics: {
      revenue: number
      jobs: number
      customers: number
      invoices: number
    }
  }>
}

export interface RevenueTrend {
  date: string
  revenue: number
}

export const corporateApi = createApi({
  reducerPath: 'corporateApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${env.apiBaseUrl}/corporate`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token')
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Dashboard', 'Metrics', 'Trends'],
  endpoints: (builder) => ({
    // Get aggregated dashboard data
    getDashboard: builder.query<DashboardData, void>({
      query: () => '/dashboard',
      providesTags: ['Dashboard'],
    }),

    // Get corporate account metrics
    getCorporateMetrics: builder.query<
      { success: boolean; summary: CorporateMetrics; businesses: BusinessMetrics[] },
      { accountId: string }
    >({
      query: ({ accountId }) => `/accounts/${accountId}/metrics`,
      providesTags: ['Metrics'],
    }),

    // Get revenue trends
    getRevenueTrends: builder.query<
      { success: boolean; trends: RevenueTrend[]; period: { start: string; end: string } },
      { accountId: string; startDate?: string; endDate?: string }
    >({
      query: ({ accountId, startDate, endDate }) => {
        const params = new URLSearchParams()
        if (startDate) params.append('startDate', startDate)
        if (endDate) params.append('endDate', endDate)
        return `/accounts/${accountId}/revenue-trends?${params.toString()}`
      },
      providesTags: ['Trends'],
    }),
  }),
})

export const {
  useGetDashboardQuery,
  useGetCorporateMetricsQuery,
  useGetRevenueTrendsQuery,
} = corporateApi

